import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ROLE_LABELS, DEPARTMENTS } from './consoleUtils';

const EMPTY = { full_name: '', email: '', phone_number: '', role: 'design', department: 'Design', temp_password: '' };
const selectCls = 'flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm';

export const AccountFormDialog = ({ open, account, roles, onClose, onSubmit }) => {
  const [form, setForm] = useState(EMPTY);
  const [applyTemplate, setApplyTemplate] = useState(false);
  const editing = !!account;

  useEffect(() => {
    if (!open) return;
    setForm(account ? { ...EMPTY, ...account, phone_number: account.phone_number || '' } : EMPTY);
    setApplyTemplate(false);
  }, [open, account]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const roleChanged = editing && form.role !== account.role;

  const submit = (e) => {
    e.preventDefault();
    onSubmit(editing
      ? { full_name: form.full_name, phone_number: form.phone_number, role: form.role, department: form.department, apply_role_template: roleChanged && applyTemplate }
      : form);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="account-form-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit account' : 'Create account'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Full name</Label>
            <Input value={form.full_name} onChange={set('full_name')} required data-testid="input-account-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={set('email')} required disabled={editing} data-testid="input-account-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input value={form.phone_number} onChange={set('phone_number')} data-testid="input-account-phone" />
          </div>
          <div className="space-y-1.5">
            <Label>Role (access template)</Label>
            <select className={selectCls} value={form.role} onChange={set('role')} data-testid="select-account-role">
              {roles.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Department</Label>
            <select className={selectCls} value={form.department || ''} onChange={set('department')} data-testid="select-account-department">
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          {!editing && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Temporary password</Label>
              <Input value={form.temp_password} onChange={set('temp_password')} placeholder="Leave blank to generate one" data-testid="input-account-temp-password" />
              <p className="text-[11px] text-muted-foreground">At least 8 characters. The employee must change it on first sign-in.</p>
            </div>
          )}
          {roleChanged && (
            <label className="sm:col-span-2 flex items-center gap-2 text-xs" data-testid="label-apply-template">
              <input type="checkbox" checked={applyTemplate} onChange={e => setApplyTemplate(e.target.checked)} data-testid="checkbox-apply-template" />
              Reset page & tab access to the {ROLE_LABELS[form.role]} defaults
            </label>
          )}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" data-testid="btn-submit-account">{editing ? 'Save changes' : 'Create account'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
